import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usersApi } from '../services/api';
import { useAuthStore } from '../store/auth.store';
import { useTenantContextStore } from '../store/tenant-context.store';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Users,
  Plus,
  Search,
  Edit2,
  Trash2,
  Mail,
  Phone,
  Shield,
  ShieldCheck,
  ShieldAlert,
  UserCheck,
  UserX,
  X,
  Loader2,
  AlertCircle,
  Check,
  Send,
  Eye,
  EyeOff,
  RefreshCw,
  Truck,
  Key,
} from 'lucide-react';
import clsx from 'clsx';
import { useBreakpoint } from '../hooks/useBreakpoint';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'MANAGER' | 'OPERATOR' | 'VIEWER' | 'DRIVER';
  isActive: boolean;
  createdAt: string;
  lastLoginAt?: string;
  twoFactorEnabled?: boolean;
}

const roleLabels: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  SUPER_ADMIN: { label: 'Super Admin', color: 'text-red-400 bg-red-500/20', icon: ShieldAlert },
  ADMIN: { label: 'Administrateur', color: 'text-purple-400 bg-purple-500/20', icon: ShieldCheck },
  MANAGER: { label: 'Manager', color: 'text-cyan-400 bg-cyan-500/20', icon: Shield },
  OPERATOR: { label: 'Opérateur', color: 'text-green-400 bg-green-500/20', icon: UserCheck },
  VIEWER: { label: 'Lecteur', color: 'text-gray-500 bg-gray-200', icon: Eye },
  DRIVER: { label: 'Conducteur', color: 'text-amber-700 bg-amber-100', icon: Truck },
};

const roles = [
  { value: 'ADMIN', label: 'Administrateur', description: 'Accès complet sauf gestion du tenant' },
  { value: 'MANAGER', label: 'Manager', description: 'Gestion des véhicules et conducteurs' },
  { value: 'OPERATOR', label: 'Opérateur', description: 'Visualisation et rapports' },
  { value: 'VIEWER', label: 'Lecteur', description: 'Lecture seule' },
  { value: 'DRIVER', label: 'Conducteur', description: 'App mobile — voit uniquement ses missions du jour' },
];

export default function UsersPage() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuthStore();
  const queryClient = useQueryClient();
  const { selectedTenant } = useTenantContextStore();
  const tenantKey = selectedTenant?.id || 'default';
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  const { isMobile } = useBreakpoint();

  const isAdmin = currentUser?.role === 'ADMIN' || currentUser?.role === 'SUPER_ADMIN';

  // Fetch users
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', tenantKey],
    queryFn: () => usersApi.list({ limit: 100 }),
  });
  // Filter out SUPER_ADMIN users (they should not be visible to tenant admins)
  const users: User[] = (usersData?.data?.data || []).filter(
    (u: User) => u.role !== 'SUPER_ADMIN'
  );

  // Filter users
  const filteredUsers = users.filter(
    (u) =>
      u.firstName.toLowerCase().includes(search.toLowerCase()) ||
      u.lastName.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase())
  );

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowDeleteConfirm(null);
    },
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      usersApi.update(id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setShowModal(true);
  };

  const handleDelete = (id: string) => {
    setShowDeleteConfirm(id);
  };

  const handleToggleActive = (user: User) => {
    toggleActiveMutation.mutate({ id: user.id, isActive: !user.isActive });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
  };

  const stats = {
    total: users.length,
    active: users.filter((u) => u.isActive).length,
    admins: users.filter((u) => u.role === 'ADMIN').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('users.title')}</h1>
          <p className="text-gray-500 mt-1">{t('users.subtitle')}</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 btn-primary"
          >
            <Plus className="h-5 w-5" />
            {t('users.add_user')}
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="glass-card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-cyan-500/20">
              <Users className="h-5 w-5 text-cyan-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-xs text-gray-500">{t('users.stat_total')}</p>
            </div>
          </div>
        </div>
        <div className="glass-card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/20">
              <UserCheck className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
              <p className="text-xs text-gray-500">{t('users.stat_active')}</p>
            </div>
          </div>
        </div>
        <div className="glass-card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <ShieldCheck className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.admins}</p>
              <p className="text-xs text-gray-500">{t('users.stat_admins')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="glass-card p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder={t('users.search_placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
          />
        </div>
      </div>

      {/* Users table */}
      <div className="glass-card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-16">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">{t('users.no_user_found')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('users.th_user')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('users.th_role')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('users.th_status')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    {t('users.th_last_login')}
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    {t('users.th_created_at')}
                  </th>
                  {isAdmin && (
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('users.th_actions')}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map((user) => {
                  const roleConfig = roleLabels[user.role] || roleLabels.VIEWER;
                  const RoleIcon = roleConfig.icon;
                  const isCurrentUser = user.id === currentUser?.id;
                  // On peut supprimer/désactiver un admin sauf si c'est soi-même
                  const canModify = !isCurrentUser;

                  return (
                    <tr key={user.id} className="hover:bg-gray-100/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white font-medium">
                            {user.firstName[0]}{user.lastName[0]}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 flex items-center gap-2">
                              {user.firstName} {user.lastName}
                              {isCurrentUser && (
                                <span className="text-xs text-cyan-400">{t('users.you_marker')}</span>
                              )}
                              {isMobile && (
                                <span className={clsx('text-xs px-1.5 py-0.5 rounded', roleConfig.color)}>
                                  {roleConfig.label}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={clsx(
                          'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg',
                          roleConfig.color
                        )}>
                          <RoleIcon className="h-3.5 w-3.5" />
                          {roleConfig.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {user.isActive ? (
                          <span className="inline-flex items-center gap-1.5 text-sm text-green-400">
                            <span className="w-2 h-2 rounded-full bg-green-500" />
                            {t('users.active')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-sm text-gray-400">
                            <span className="w-2 h-2 rounded-full bg-gray-400" />
                            {t('users.inactive')}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 hidden md:table-cell">
                        {user.lastLoginAt
                          ? format(new Date(user.lastLoginAt), 'dd MMM yyyy HH:mm', { locale: fr })
                          : t('users.never')}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 hidden md:table-cell">
                        {format(new Date(user.createdAt), 'dd MMM yyyy', { locale: fr })}
                      </td>
                      {isAdmin && (
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => handleEdit(user)}
                              className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                              title={t('users.edit')}
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleToggleActive(user)}
                              disabled={!canModify}
                              className={clsx(
                                'p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                                user.isActive
                                  ? 'text-gray-500 hover:text-orange-600 hover:bg-orange-50'
                                  : 'text-gray-500 hover:text-green-600 hover:bg-green-50'
                              )}
                              title={isCurrentUser ? t('users.cannot_disable_self') : (user.isActive ? t('users.deactivate') : t('users.activate'))}
                            >
                              {user.isActive ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                            </button>
                            <button
                              onClick={() => handleDelete(user.id)}
                              disabled={!canModify}
                              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title={isCurrentUser ? t('users.cannot_delete_self') : t('users.delete')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <UserModal
          key={editingUser?.id || 'new'}
          user={editingUser}
          onClose={handleCloseModal}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            handleCloseModal();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(null)} />
          <div className="relative bg-white rounded-2xl border border-gray-200 p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 rounded-xl bg-red-500/20">
                <AlertCircle className="h-6 w-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('users.delete_title')}</h3>
                <p className="text-sm text-gray-500">{t('users.delete_irreversible')}</p>
              </div>
            </div>
            <p className="text-gray-600 mb-6">
              {t('users.delete_confirm')}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => deleteMutation.mutate(showDeleteConfirm)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 disabled:opacity-50 flex items-center gap-2"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UserModal({
  user,
  onClose,
  onSuccess,
}: {
  user: User | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState({
    email: user?.email || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
    role: user?.role || 'OPERATOR',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditing = !!user;

  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, password });
    setShowPassword(true);
  };

  // Extract a user-friendly error message from an axios/zod response.
  // Backend may return { error, code, details? } where details is a fieldErrors map.
  const extractError = (error: any): string => {
    const data = error?.response?.data;
    if (!data) return 'Erreur réseau — vérifie ta connexion';
    // Zod validation error → flatten field errors into a readable line
    if (data.details && typeof data.details === 'object') {
      const fieldMsgs: string[] = [];
      for (const [field, messages] of Object.entries(data.details)) {
        if (Array.isArray(messages) && messages.length > 0) {
          fieldMsgs.push(`${field}: ${(messages as string[]).join(', ')}`);
        }
      }
      if (fieldMsgs.length > 0) return fieldMsgs.join(' · ');
    }
    return data.error || data.message || 'Erreur inconnue';
  };

  const createMutation = useMutation({
    mutationFn: (data: typeof formData & { sendInvite?: boolean }) => usersApi.create(data),
    onSuccess,
    onError: (error: any) => {
      setErrors({ submit: extractError(error) });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<typeof formData>) => usersApi.update(user!.id, data),
    onSuccess,
    onError: (error: any) => {
      setErrors({ submit: extractError(error) });
    },
  });

  const handleSubmit = (e: React.FormEvent, sendInvite: boolean = false) => {
    e.preventDefault();
    setErrors({});

    // Validation — must mirror backend strongPassword rules
    const newErrors: Record<string, string> = {};
    if (!formData.email) newErrors.email = 'Email requis';
    if (!formData.firstName) newErrors.firstName = 'Prénom requis';
    if (!formData.lastName) newErrors.lastName = 'Nom requis';
    if (!isEditing) {
      if (!formData.password) {
        newErrors.password = 'Mot de passe requis';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Minimum 8 caractères';
      } else if (!/[A-Z]/.test(formData.password)) {
        newErrors.password = 'Doit contenir au moins une majuscule';
      } else if (!/[a-z]/.test(formData.password)) {
        newErrors.password = 'Doit contenir au moins une minuscule';
      } else if (!/[0-9]/.test(formData.password)) {
        newErrors.password = 'Doit contenir au moins un chiffre';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (isEditing) {
      updateMutation.mutate({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone || undefined,
        role: formData.role,
      });
    } else {
      createMutation.mutate({ ...formData, sendInvite });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl border border-gray-200 w-full max-w-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-cyan-500/20">
              <Users className="h-5 w-5 text-cyan-400" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditing ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {errors.submit && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-2 text-sm text-red-400">
              <AlertCircle className="h-4 w-4" />
              {errors.submit}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Prénom</label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className={clsx(
                  'w-full px-4 py-2.5 bg-gray-100 border rounded-xl text-gray-900 focus:ring-1',
                  errors.firstName
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                    : 'border-gray-300 focus:border-cyan-500 focus:ring-cyan-500/20'
                )}
              />
              {errors.firstName && (
                <p className="mt-1 text-xs text-red-400">{errors.firstName}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Nom</label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className={clsx(
                  'w-full px-4 py-2.5 bg-gray-100 border rounded-xl text-gray-900 focus:ring-1',
                  errors.lastName
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                    : 'border-gray-300 focus:border-cyan-500 focus:ring-cyan-500/20'
                )}
              />
              {errors.lastName && (
                <p className="mt-1 text-xs text-red-400">{errors.lastName}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={clsx(
                  'w-full pl-11 pr-4 py-2.5 bg-gray-100 border rounded-xl text-gray-900 focus:ring-1',
                  errors.email
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                    : 'border-gray-300 focus:border-cyan-500 focus:ring-cyan-500/20'
                )}
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-xs text-red-400">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Téléphone</label>
            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+33 6 12 34 56 78"
                className="w-full pl-11 pr-4 py-2.5 bg-gray-100 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20"
              />
            </div>
            <p className="mt-1 text-xs text-gray-400">Requis pour recevoir les alertes SMS</p>
          </div>

          {!isEditing && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-600">Mot de passe</label>
                <button
                  type="button"
                  onClick={generatePassword}
                  className="flex items-center gap-1 text-xs text-primary-500 hover:text-primary-600 transition-colors"
                >
                  <RefreshCw className="h-3 w-3" />
                  Générer
                </button>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Min 8 car · 1 maj · 1 min · 1 chiffre"
                  className={clsx(
                    'w-full px-4 py-2.5 pr-10 bg-gray-100 border rounded-xl text-gray-900 focus:ring-1',
                    errors.password
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                      : 'border-gray-300 focus:border-cyan-500 focus:ring-cyan-500/20'
                  )}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-red-400">{errors.password}</p>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Role</label>
            <div className="space-y-2">
              {roles.map((role) => (
                <label
                  key={role.value}
                  className={clsx(
                    'flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors',
                    formData.role === role.value
                      ? 'bg-cyan-500/10 border-cyan-500/30'
                      : 'bg-gray-100/50 border-gray-300 hover:border-gray-400'
                  )}
                >
                  <input
                    type="radio"
                    name="role"
                    value={role.value}
                    checked={formData.role === role.value}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as User['role'] })}
                    className="sr-only"
                  />
                  <div className={clsx(
                    'w-4 h-4 rounded-full border-2 flex items-center justify-center',
                    formData.role === role.value
                      ? 'border-cyan-500 bg-cyan-500'
                      : 'border-gray-400'
                  )}>
                    {formData.role === role.value && (
                      <Check className="h-2.5 w-2.5 text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{role.label}</p>
                    <p className="text-xs text-gray-500">{role.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Password reset (edit only) */}
          {isEditing && user && (
            <UserPasswordResetSection userId={user.id} />
          )}

          {/* 2FA reset (edit only) */}
          {isEditing && user && (
            <User2FAResetSection userId={user.id} twoFactorEnabled={user.twoFactorEnabled} />
          )}

          {/* Footer */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-100 transition-colors"
            >
              Annuler
            </button>
            {isEditing ? (
              <button
                type="submit"
                disabled={isPending}
                className="flex-1 px-4 py-2.5 btn-primary flex items-center justify-center gap-2"
              >
                {isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    Enregistrer
                  </>
                )}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, false)}
                  disabled={isPending}
                  className="flex-1 px-4 py-2.5 btn-secondary flex items-center justify-center gap-2"
                >
                  {isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      Enregistrer
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, true)}
                  disabled={isPending}
                  className="flex-1 px-4 py-2.5 btn-primary flex items-center justify-center gap-2"
                >
                  {isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Inviter
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UserPasswordResetSection — collapsible inline panel in the user edit modal
// that lets an admin set or reset the user's login password. Used mostly for:
// - Drivers migrated with placeholder emails (need real credentials)
// - Lost password situations where an admin needs to restore access
// ---------------------------------------------------------------------------

function UserPasswordResetSection({ userId }: { userId: string }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const mutation = useMutation({
    mutationFn: () => usersApi.setPassword(userId, password),
    onSuccess: () => {
      setResult({ type: 'success', message: 'Mot de passe défini — l\'utilisateur peut maintenant se connecter' });
      setPassword('');
      setShowPwd(false);
    },
    onError: (err: any) => {
      const data = err?.response?.data;
      let msg = data?.error || data?.message || 'Échec de la définition du mot de passe';
      if (data?.details && typeof data.details === 'object') {
        const fieldMsgs: string[] = [];
        for (const [field, messages] of Object.entries(data.details)) {
          if (Array.isArray(messages)) fieldMsgs.push(`${field}: ${(messages as string[]).join(', ')}`);
        }
        if (fieldMsgs.length > 0) msg = fieldMsgs.join(' · ');
      }
      setResult({ type: 'error', message: msg });
    },
  });

  const generate = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let p = 'A' + 'b' + '1';
    for (let i = 0; i < 9; i++) p += chars.charAt(Math.floor(Math.random() * chars.length));
    p = p.split('').sort(() => Math.random() - 0.5).join('');
    setPassword(p);
    setShowPwd(true);
  };

  const validate = (): string | null => {
    if (password.length < 8) return 'Min 8 caractères';
    if (!/[A-Z]/.test(password)) return 'Doit contenir une majuscule';
    if (!/[a-z]/.test(password)) return 'Doit contenir une minuscule';
    if (!/[0-9]/.test(password)) return 'Doit contenir un chiffre';
    return null;
  };

  const submit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setResult(null);
    const err = validate();
    if (err) {
      setResult({ type: 'error', message: err });
      return;
    }
    mutation.mutate();
  };

  return (
    <div className="border border-gray-200 rounded-xl">
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); setOpen(!open); }}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 rounded-xl"
      >
        <div className="flex items-center gap-2">
          <Key className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-900">
            {open ? 'Fermer' : 'Définir / réinitialiser le mot de passe'}
          </span>
        </div>
        <span className="text-xs text-gray-400">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="p-4 pt-2 border-t border-gray-200 space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-gray-700">Nouveau mot de passe</label>
              <button
                type="button"
                onClick={generate}
                className="text-xs text-primary-500 hover:text-primary-600 flex items-center gap-1"
              >
                <RefreshCw className="h-3 w-3" />
                Générer
              </button>
            </div>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 8 car · 1 maj · 1 min · 1 chiffre"
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm font-mono"
              />
              <button
                type="button"
                onClick={() => setShowPwd(!showPwd)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {result && (
            <div
              className={`text-xs p-2 rounded ${
                result.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-700'
                  : 'bg-red-50 border border-red-200 text-red-700'
              }`}
            >
              {result.message}
            </div>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={mutation.isPending}
            className="w-full py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Key className="h-4 w-4" />}
            Définir le mot de passe
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// User2FAResetSection — collapsible inline panel in the user edit modal that
// lets an admin reset (disable) a user's two-factor authentication. Recovery
// path for a user who lost their authenticator device: clears the TOTP/Duo
// secret and backup codes so they can sign in with just their password and
// re-enroll 2FA afterwards. Destructive, so it requires an inline confirmation.
// ---------------------------------------------------------------------------

function User2FAResetSection({ userId, twoFactorEnabled }: { userId: string; twoFactorEnabled?: boolean }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const mutation = useMutation({
    mutationFn: () => usersApi.reset2FA(userId),
    onSuccess: () => {
      setResult({
        type: 'success',
        message: 'Le 2FA a été réinitialisé — l\'utilisateur se connectera avec son mot de passe seul, puis pourra réactiver le 2FA.',
      });
      setConfirming(false);
      setDone(true);
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: any) => {
      const data = err?.response?.data;
      setResult({ type: 'error', message: data?.error || data?.message || 'Échec de la réinitialisation du 2FA' });
      setConfirming(false);
    },
  });

  // Once reset, treat as disabled regardless of the (stale) prop value.
  const isEnabled = !!twoFactorEnabled && !done;

  return (
    <div className="border border-gray-200 rounded-xl">
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); setOpen(!open); }}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-50 rounded-xl"
      >
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-900">
            {open ? 'Fermer' : 'Réinitialiser le 2FA'}
          </span>
        </div>
        <span className="text-xs text-gray-400">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="p-4 pt-2 border-t border-gray-200 space-y-3">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500">Statut actuel :</span>
            {isEnabled ? (
              <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                2FA activé
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-gray-400 font-medium">
                <span className="w-2 h-2 rounded-full bg-gray-400" />
                2FA non activé
              </span>
            )}
          </div>

          <p className="text-xs text-gray-500">
            Réinitialise (désactive) la double authentification de l'utilisateur. À utiliser s'il a perdu
            son téléphone ou son application d'authentification. Il pourra ensuite se reconnecter avec son
            mot de passe seul, puis réactiver le 2FA depuis ses paramètres.
          </p>

          {result && (
            <div
              className={`text-xs p-2 rounded ${
                result.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-700'
                  : 'bg-red-50 border border-red-200 text-red-700'
              }`}
            >
              {result.message}
            </div>
          )}

          {!done && (
            confirming ? (
              <div className="space-y-2">
                <div className="text-xs p-2 rounded bg-amber-50 border border-amber-200 text-amber-700 flex items-start gap-1.5">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>Confirmer la réinitialisation du 2FA de cet utilisateur ?</span>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); setConfirming(false); }}
                    className="flex-1 py-2 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-100"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); setResult(null); mutation.mutate(); }}
                    disabled={mutation.isPending}
                    className="flex-1 py-2 bg-red-500 hover:bg-red-600 disabled:bg-gray-300 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                  >
                    {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldAlert className="h-4 w-4" />}
                    Confirmer la réinitialisation
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); setResult(null); setConfirming(true); }}
                className="w-full py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
              >
                <ShieldAlert className="h-4 w-4" />
                Réinitialiser le 2FA
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}
